/**
 * カツオポータル専用レイアウト
 * 方針（2026-10-19 利用者フィードバック反映）: 装飾より使いやすさを最優先にする。
 * - 白基調・高コントラスト（濃い文字・アクセントは落ち着いた青緑1色のみ。グラデーション不使用）
 * - 上部: モジュールタブ（未読バッジ）＋「船員勤怠」メニュー常設（カツオ勤怠側へ1クリックで移動）
 * - ポータル配下の既存ページは .pv-root のスコープ付きCSSで一括リスキンする
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Home, Megaphone, MessagesSquare, BookOpen, Receipt, FileCheck, Clock,
  LogOut, Bell, Ship, ChevronDown, ClipboardList, Moon, Palmtree, Users, LayoutDashboard, CalendarDays, Printer,
} from 'lucide-react';
import * as api from '../../services/api';
import { clearToken } from '../../services/api';
import { isDemoHost } from '../../utils/brand';
import { pushSupported, isPushEnabled, enablePush } from '../../services/push';

/** デザイントークン（白基調・高コントラスト・アクセント1色） */
export const PV = {
  accent: '#0f766e',        // 青緑（カツオ勤怠の青とは別系統・白地で読みやすい）
  accentDark: '#0c5f59',
  accentSoft: '#e6f4f1',
  gradient: 'linear-gradient(135deg, #0f766e, #0c5f59)', // ボタン用の控えめな濃淡のみ
  bg: '#f6f7f8',
  text: '#1a1c1f',
  textSub: '#50545b',
  border: '#e3e6ea',
  cardShadow: '0 1px 2px rgba(0,0,0,0.05)',
  font: "'Segoe UI', 'Yu Gothic UI', 'Hiragino Sans', 'Meiryo', sans-serif",
};

/** 名前からアバター色（人ごとに色を変える。濃色＋白文字で視認性を確保） */
const AVATAR_COLORS = ['#0f766e', '#1d4ed8', '#9333ea', '#be185d', '#b45309', '#15803d', '#0369a1', '#7c2d12'];
export function avatarColor(name: string | null | undefined): string {
  const s = name ?? '';
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length]!;
}

export const Avatar: React.FC<{ name: string | null | undefined; size?: number }> = ({ name, size = 36 }) => (
  <span style={{
    width: size, height: size, borderRadius: '50%', flexShrink: 0,
    background: avatarColor(name), color: '#fff',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    fontSize: size * 0.36, fontWeight: 700, letterSpacing: '0.02em', userSelect: 'none',
  }}>
    {(name ?? '？').replace(/\s/g, '').slice(0, 2)}
  </span>
);

interface NavSummary {
  board_unread: number; chat_unread: number; manuals_unread: number;
  approvals_expenses: number; approvals_requests: number;
  my_expenses_active: number; my_requests_pending: number;
  is_admin: boolean;
}

function NotifyBell() {
  const [off, setOff] = useState(false);
  useEffect(() => {
    if (!pushSupported()) return;
    isPushEnabled().then(on => setOff(!on)).catch(() => { /* noop */ });
  }, []);
  if (!off) return null;
  return (
    <button
      title="承認依頼・お知らせ・チャットをPC通知で受け取る"
      onClick={async () => {
        const r = await enablePush();
        if (r.ok) setOff(false); else alert(r.reason ?? '通知を有効化できませんでした');
      }}
      style={{
        display: 'flex', alignItems: 'center', gap: 6, padding: '8px 13px', borderRadius: 8,
        border: '1px solid #f0c36d', background: '#fff8e7', color: '#8a5a00', fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
      }}>
      <Bell size={14} /> 通知をON
    </button>
  );
}

/** ポータル配下の共通リスキン（既存の .card / .btn / table 等を読みやすく統一） */
const SCOPED_CSS = `
.pv-root { font-family: ${PV.font}; color: ${PV.text}; --primary: ${PV.accent}; font-size: 14px; }
.pv-root .page-wrap { max-width: 1160px; margin: 0 auto; width: 100%; box-sizing: border-box; }
.pv-root .page-title { font-size: 20px; font-weight: 700; color: ${PV.text}; letter-spacing: -0.2px; }
.pv-root .card { background: #fff; border: 1px solid ${PV.border}; border-radius: 10px; box-shadow: ${PV.cardShadow}; }
.pv-root .btn-p { background: ${PV.accent}; border: none; border-radius: 8px; color: #fff; padding: 9px 18px; font-weight: 700; font-size: 14px; cursor: pointer; font-family: inherit; }
.pv-root .btn-p:hover { background: ${PV.accentDark}; }
.pv-root .btn-p:disabled { background: #c3c8cd; cursor: not-allowed; }
.pv-root .btn { background: #fff; border: 1px solid #c8cdd3; border-radius: 8px; padding: 8px 14px; color: ${PV.text}; cursor: pointer; font-size: 13.5px; font-weight: 600; font-family: inherit; }
.pv-root .btn:hover { background: #f1f3f5; }
.pv-root table { width: 100%; border-collapse: collapse; }
.pv-root th { background: #f1f3f5; color: #3d4248; font-size: 12.5px; font-weight: 700; text-align: left; padding: 10px 12px; border-bottom: 1px solid ${PV.border}; }
.pv-root td { padding: 11px 12px; border-bottom: 1px solid #eef0f2; font-size: 14px; color: ${PV.text}; }
.pv-root tbody tr:hover td { background: #f3faf8; }
.pv-root .empty { color: ${PV.textSub}; font-size: 14px; text-align: center; }
.pv-root .loading { color: ${PV.textSub}; font-size: 14px; text-align: center; padding: 24px; }
.pv-root input, .pv-root select, .pv-root textarea { font-family: inherit; }
`;

const NAV: Array<{ path: string; icon: React.ElementType; label: string }> = [
  { path: '/portal', icon: Home, label: 'ホーム' },
  { path: '/portal/board', icon: Megaphone, label: '連絡' },
  { path: '/portal/chat', icon: MessagesSquare, label: 'チャット' },
  { path: '/portal/manuals', icon: BookOpen, label: 'マニュアル' },
  { path: '/portal/expenses', icon: Receipt, label: '経費' },
  { path: '/portal/requests', icon: FileCheck, label: '申請' },
  { path: '/portal/office', icon: Clock, label: '陸上勤怠' },
];

/** 船員勤怠（カツオ勤怠側）への常設メニュー。
 *  よく使う「勤怠・休息・休暇」に絞る（2026-10-19 指示）。その他は管理画面トップから。 */
const CREW_MENU: Array<{ path: string; icon: React.ElementType; label: string; note?: string }> = [
  { path: '/attendance', icon: ClipboardList, label: '勤怠一覧', note: '打刻の確認・修正' },
  { path: '/rest-hours', icon: Moon, label: '休息時間' },
  { path: '/leave', icon: Palmtree, label: '休暇・休日' },
  { path: '/portal/comp-days', icon: CalendarDays, label: '補償休日', note: '付与・取得・残' },
  { path: '/portal/certificates', icon: Printer, label: '書類の作成', note: '証明書・届出書の様式' },
  { path: '/dashboard', icon: LayoutDashboard, label: '管理画面トップ', note: 'そのほか全機能' },
];

export default function PortalLayout({ children, wide }: { children: React.ReactNode; wide?: boolean }) {
  const nav = useNavigate();
  const loc = useLocation();
  const [sum, setSum] = useState<NavSummary | null>(null);
  const [crewMenuOpen, setCrewMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const user = (() => { try { return JSON.parse(localStorage.getItem('admin_user') ?? '{}'); } catch { return {}; } })();

  const load = useCallback(() => {
    api.get<{ data: NavSummary }>('/portal/summary').then(r => setSum(r.data)).catch(() => { /* noop */ });
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(load, 45_000);
    return () => clearInterval(t);
  }, [load]);

  // メニュー外クリックで閉じる
  useEffect(() => {
    if (!crewMenuOpen) return;
    const close = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setCrewMenuOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [crewMenuOpen]);

  const badgeOf = (path: string): number => {
    if (!sum) return 0;
    switch (path) {
      case '/portal/board': return sum.board_unread;
      case '/portal/chat': return sum.chat_unread;
      case '/portal/manuals': return sum.manuals_unread;
      case '/portal/expenses': return sum.is_admin ? sum.approvals_expenses : sum.my_expenses_active;
      case '/portal/requests': return sum.is_admin ? sum.approvals_requests : sum.my_requests_pending;
      default: return 0;
    }
  };

  const portalName = isDemoHost() ? 'カツオポータル' : '社内ポータル';

  return (
    <div className="pv-root" style={{ minHeight: '100vh', width: '100%', background: PV.bg, display: 'flex', flexDirection: 'column' }}>
      <style>{SCOPED_CSS}</style>

      {/* トップバー */}
      <header style={{
        background: '#fff', borderBottom: `1px solid ${PV.border}`, position: 'sticky', top: 0, zIndex: 50,
      }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 20px', display: 'flex', alignItems: 'center', gap: 8, height: 58 }}>
          {/* ブランド */}
          <button onClick={() => nav('/portal')} style={{ display: 'flex', alignItems: 'center', gap: 9, border: 'none', background: 'none', cursor: 'pointer', padding: 0, marginRight: 10 }}>
            <span style={{
              width: 34, height: 34, borderRadius: 9, background: PV.accent,
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <Home size={18} color="#fff" strokeWidth={2.2} />
            </span>
            <span style={{ fontSize: 16, fontWeight: 800, color: PV.text, letterSpacing: '-0.2px', whiteSpace: 'nowrap' }}>{portalName}</span>
          </button>

          {/* モジュールタブ */}
          <nav style={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, overflowX: 'auto' }}>
            {NAV.map(n => {
              const active = n.path === '/portal' ? loc.pathname === '/portal' : loc.pathname.startsWith(n.path);
              const Icon = n.icon;
              const badge = badgeOf(n.path);
              return (
                <button key={n.path} onClick={() => nav(n.path)}
                  style={{
                    position: 'relative', display: 'flex', alignItems: 'center', gap: 6,
                    padding: '0 13px', height: 58, border: 'none', cursor: 'pointer',
                    background: active ? PV.accentSoft : 'none',
                    color: active ? PV.accentDark : '#3d4248', fontSize: 14, fontWeight: active ? 800 : 600,
                    borderBottom: active ? `3px solid ${PV.accent}` : '3px solid transparent',
                    borderTop: '3px solid transparent', whiteSpace: 'nowrap', fontFamily: 'inherit',
                  }}>
                  <Icon size={16} strokeWidth={active ? 2.4 : 2} />
                  {n.label}
                  {badge > 0 && (
                    <span style={{
                      background: '#d93025', color: '#fff', borderRadius: 999, fontSize: 10.5, fontWeight: 700,
                      minWidth: 17, height: 17, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px',
                    }}>{badge > 99 ? '99+' : badge}</span>
                  )}
                </button>
              );
            })}
          </nav>

          {/* 船員勤怠（カツオ勤怠）メニュー: 陸上管理者の主業務へすぐ移動できる常設の入口 */}
          <div ref={menuRef} style={{ position: 'relative' }}>
            <button onClick={() => setCrewMenuOpen(o => !o)}
              style={{
                display: 'flex', alignItems: 'center', gap: 7, padding: '9px 14px', borderRadius: 8,
                border: `1.5px solid ${PV.accent}`, background: crewMenuOpen ? PV.accentSoft : '#fff',
                color: PV.accentDark, fontSize: 13.5, fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap',
              }}>
              <Ship size={15} /> 船員勤怠 <ChevronDown size={13} style={{ transform: crewMenuOpen ? 'rotate(180deg)' : undefined, transition: 'transform .15s' }} />
            </button>
            {crewMenuOpen && (
              <div style={{
                position: 'absolute', right: 0, top: 'calc(100% + 6px)', width: 260,
                background: '#fff', border: `1px solid ${PV.border}`, borderRadius: 10,
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)', padding: 6, zIndex: 100,
              }}>
                <div style={{ fontSize: 11.5, fontWeight: 700, color: PV.textSub, padding: '7px 10px 5px' }}>カツオ勤怠（船員の管理）へ移動</div>
                {CREW_MENU.map(m => {
                  const Icon = m.icon;
                  return (
                    <button key={m.path} onClick={() => { setCrewMenuOpen(false); nav(m.path); }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left',
                        padding: '10px 10px', border: 'none', borderRadius: 8, background: 'none', cursor: 'pointer', fontFamily: 'inherit',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = '#f1f3f5'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}>
                      <Icon size={16} color={PV.accent} />
                      <span style={{ fontSize: 13.5, fontWeight: 700, color: PV.text }}>{m.label}</span>
                      {m.note && <span style={{ fontSize: 11, color: PV.textSub, marginLeft: 'auto' }}>{m.note}</span>}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* ユーザー管理は船員勤怠に限らない会社全体の管理なので、ポータル直下に常設する */}
          {['system_admin', 'shore_admin'].includes(user?.user_role ?? '') && (
            <button onClick={() => nav('/portal/users')} title="社員・船員の登録・編集"
              style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '9px 13px', borderRadius: 8,
                border: '1px solid #c8cdd3', background: loc.pathname === '/portal/users' ? PV.accentSoft : '#fff',
                color: '#3d4248', fontSize: 13, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
              }}>
              <Users size={15} /> ユーザー管理
            </button>
          )}
          <NotifyBell />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button onClick={() => nav('/portal/profile')} title="マイページ"
              style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0, display: 'flex' }}>
              <Avatar name={user?.user_name} size={32} />
            </button>
            <button onClick={() => clearToken()} title="ログアウト"
              style={{ display: 'flex', alignItems: 'center', border: 'none', background: 'none', color: '#6b7076', cursor: 'pointer', padding: 4 }}>
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </header>

      {/* 本文 */}
      <main style={{ flex: 1, width: '100%', maxWidth: wide ? 1280 : 1160, margin: '0 auto', padding: '20px 20px 48px', boxSizing: 'border-box' }}>
        {children}
      </main>
    </div>
  );
}
